import clsx from 'clsx';
import React, { useRef } from 'react';
import { IProductVariant } from '../../../services/helper/productInterfaces';
import { useWordClamp } from './hooks/useWordClamp';

export default function ProductNameAndPrice({
  product,
}: {
  product: IProductVariant;
  namePriceClassName?: string;
}): JSX.Element {
  if (product.name == undefined) return <></>;
  else if (isNaN(product.price)) {
    product.price = 0;
  }
  const price = product?.price?.toFixed(2);
  const prevPrice = product?.previousPrice?.toFixed(0) ?? '';
  const prodType = product?.categories?.[0]?.key;
  const productName = product?.nameFriendly ?? product?.name ?? '';
  
  const ref = useRef<HTMLSpanElement>(null);
  const text = capitalizeFirstLetter(productName);
  const clampTxt = useWordClamp(ref, text, {
    lines: 2,
    ellipsis: '…',
    allowBreakOnVeryLongFirstToken: false,
    debug: false, // set true temporarily to see logs
  });

  return (
    <div
      data-testid="product-name-price"
      className='flex flex-col text-[#0D1C3D] font-medium flex-wrap xl:h-[89px] min-w-0'
    >
      {prodType != "contact_lens_models" && 
      <>
        <span className="overflow-clip h-[20px] md:h-[20px] xl:h-[28px] text-[15px] xl:text-[20px] text-center">
          {capitalizeFirstLetter(product?.brandFriendly)}
        </span>
        <span
          title={productName}
          className="overflow-clip h-[20px] md:h-[20px] xl:h-[28px] text-[15px] xl:text-[20px] text-center"
        >
          {capitalizeFirstLetter(productName)}
        </span>
      </>
      }
      {prodType === "contact_lens_models" && 
        <span
          ref={ref}
          title={productName}
          aria-label={productName}
          className={clsx(
            // Make sure it can wrap and measure correctly
            'block w-full overflow-hidden whitespace-normal text-center min-w-0',
            // Heights for two lines
            'h-[38px] md:h-[40px] xl:h-[56px]',
            // Font sizes
            'text-[15px] xl:text-[20px]',
            // Always use the two-line leading for stability
            //productName.length < 31 ? 'leading-[18px] xl:leading-[56px]':'leading-[18px] xl:leading-[26px]',
            'leading-[18px] xl:leading-[26px]',
            // Keep words intact (change to break-words if you have very long SKUs/URLs)
            'break-keep'
          )}
        >
          {clampTxt}
        </span>
      }
      
      <div className="overflow-clip h-[20px] md:h-[20px] xl:h-[28px] text-[15px] xl:text-[20px] text-center">
        {`$${price}`}
        {prevPrice && (
          <a className="text-[#878F9B] font-medium line-through justify-center mx-1 md:mx-xs">
            {`$${prevPrice}`}
          </a>
        )}
      </div>
    </div>
  );
}
export function capitalizeFirstLetter(name: string) {
  return name
    ?.toLowerCase() // Make everything lowercase first
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}
